/**
 * Caravan product card — quick-add + quick-view behaviour
 * Paired with snippets/cw-product-card.liquid + assets/cw-product-card.css (wave 26)
 *
 * Link from theme.liquid (just before </body>):
 *   {{ 'cw-product-card.js' | asset_url | script_tag }}
 *
 * Responsibilities:
 *   1. Quick add — buttons with [data-cw-quick-add="<variant_id>"] post to /cart/add.js
 *      and dispatch a 'cart:updated' window event so the existing cart drawer refreshes.
 *   2. Quick view — buttons with [data-cw-quickview="<handle>"] fetch the product JSON
 *      from /products/{handle}.js and pop a modal with image + title + price + desc + CTAs.
 *   3. Modal lifecycle — backdrop click, esc key, close button all dismiss.
 *
 * Zero dependencies. ~120 lines.
 */
(function () {
  'use strict';

  // ─── Quick add ──────────────────────────────────────────────────────
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-cw-quick-add]');
    if (!btn) return;
    e.preventDefault();
    if (btn.dataset.cwQuickAddBusy === '1') return;

    var variantId = btn.dataset.cwQuickAdd;
    if (!variantId) return;

    btn.dataset.cwQuickAddBusy = '1';
    var originalText = btn.innerHTML;
    btn.innerHTML = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        btn.innerHTML = '✓ Added';
        // Notify the rest of the theme (cart drawer should listen)
        window.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'cw-product-card' } }));
        setTimeout(function () {
          btn.innerHTML = originalText;
          btn.dataset.cwQuickAddBusy = '0';
        }, 1600);
      })
      .catch(function (err) {
        console.error('[cw-product-card] quick-add failed', err);
        btn.innerHTML = 'Error — retry';
        setTimeout(function () {
          btn.innerHTML = originalText;
          btn.dataset.cwQuickAddBusy = '0';
        }, 2200);
      });
  });

  // ─── Quick view ────────────────────────────────────────────────────
  // Ensure modal element exists (idempotent)
  function ensureModal() {
    var existing = document.querySelector('.cw-quickview');
    if (existing) return existing;
    var el = document.createElement('div');
    el.className = 'cw-quickview';
    el.setAttribute('data-open', '0');
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML =
      '<div class="cw-quickview__backdrop" data-cw-quickview-close></div>' +
      '<div class="cw-quickview__panel" role="document">' +
      '  <button type="button" class="cw-quickview__close" data-cw-quickview-close aria-label="Close">&times;</button>' +
      '  <div class="cw-quickview__media"><img src="" alt=""></div>' +
      '  <div class="cw-quickview__body">' +
      '    <small class="cw-quickview__eyebrow"></small>' +
      '    <h2 class="cw-quickview__title"></h2>' +
      '    <p class="cw-quickview__price"></p>' +
      '    <div class="cw-quickview__desc"></div>' +
      '    <div class="cw-quickview__actions">' +
      '      <button type="button" class="cw-quickview__add" data-cw-quickview-add>Add to cart</button>' +
      '      <a class="cw-quickview__view" data-cw-quickview-link>View full detail →</a>' +
      '    </div>' +
      '  </div>' +
      '</div>';
    document.body.appendChild(el);
    return el;
  }

  // Fix wave 68 (M23): focus management on modal open/close. Store trigger
  // reference at open time; restore focus to trigger on close. Initial focus
  // moves to the close button so keyboard users land inside the dialog.
  var lastTrigger = null;

  // Fix wave 68 (M18): use the shared scroll-lock helper installed by
  // cw-cart-drawer.js if present; otherwise fall back to direct mutation.
  // Prevents the cart-drawer + quickview overlay-stack from clobbering each
  // other's body.overflow state.
  function lockScroll() {
    if (window.CwScrollLock && typeof window.CwScrollLock.lock === 'function') {
      window.CwScrollLock.lock();
    } else {
      document.body.style.overflow = 'hidden';
    }
  }
  function unlockScroll() {
    if (window.CwScrollLock && typeof window.CwScrollLock.unlock === 'function') {
      window.CwScrollLock.unlock();
    } else {
      document.body.style.overflow = '';
    }
  }

  // Fix wave 68 (M21): replace the unsafe `innerHTML = (description).slice(0, 800)`
  // which could produce malformed HTML if the truncation cut a tag mid-attribute.
  // Builds a temp div, extracts textContent, truncates by character count,
  // re-renders as text. Loses bold/italic from the description but eliminates
  // both XSS-adjacent risk and the malformed-HTML rendering quirk.
  function safeDescriptionExcerpt(html, maxChars) {
    if (!html) return '';
    var temp = document.createElement('div');
    temp.innerHTML = html;
    var text = (temp.textContent || temp.innerText || '').trim();
    if (text.length > maxChars) {
      return text.slice(0, maxChars).replace(/\s+\S*$/, '') + '…';
    }
    return text;
  }

  function openModal(handle, triggerEl) {
    lastTrigger = triggerEl || document.activeElement;
    var modal = ensureModal();
    var img = modal.querySelector('.cw-quickview__media img');
    var eyebrow = modal.querySelector('.cw-quickview__eyebrow');
    var title = modal.querySelector('.cw-quickview__title');
    var price = modal.querySelector('.cw-quickview__price');
    var desc = modal.querySelector('.cw-quickview__desc');
    var addBtn = modal.querySelector('[data-cw-quickview-add]');
    var viewLink = modal.querySelector('[data-cw-quickview-link]');
    var closeBtn = modal.querySelector('[data-cw-quickview-close]');

    // Loading state
    title.textContent = 'Loading…';
    eyebrow.textContent = '';
    price.textContent = '';
    desc.textContent = '';
    img.src = '';
    img.alt = '';
    modal.setAttribute('data-open', '1');
    modal.setAttribute('aria-hidden', 'false');
    lockScroll();

    // Move focus into the modal after transition tick so screen readers
    // announce the dialog. Close button is the safest landing point.
    setTimeout(function () {
      if (closeBtn && typeof closeBtn.focus === 'function') closeBtn.focus();
    }, 60);

    fetch('/products/' + handle + '.js')
      .then(function (r) {
        if (!r.ok) throw new Error('product fetch not ok (' + r.status + ')');
        return r.json();
      })
      .then(function (p) {
        eyebrow.textContent = (p.vendor || '') + (p.type ? ' · ' + p.type : '');
        title.textContent = p.title;
        var priceText = formatMoney(p.price);
        if (p.compare_at_price && p.compare_at_price > p.price) {
          // Compose price via DOM nodes to avoid innerHTML even for static markup.
          price.textContent = '';
          var saleEl = document.createElement('strong');
          saleEl.style.color = 'var(--cw-punch, #c3122f)';
          saleEl.textContent = priceText;
          var wasEl = document.createElement('s');
          wasEl.style.opacity = '0.5';
          wasEl.style.fontWeight = '400';
          wasEl.style.marginLeft = '6px';
          wasEl.textContent = formatMoney(p.compare_at_price);
          price.appendChild(saleEl);
          price.appendChild(document.createTextNode(' '));
          price.appendChild(wasEl);
        } else {
          price.textContent = priceText;
        }
        // M21 fix: safe text-only excerpt instead of HTML slice
        desc.textContent = safeDescriptionExcerpt(p.description, 400);
        if (p.featured_image) {
          img.src = p.featured_image;
          img.alt = p.title;
        }
        viewLink.href = '/products/' + p.handle;
        var firstAvailable = (p.variants || []).find(function (v) { return v.available; });
        if (firstAvailable) {
          addBtn.disabled = false;
          addBtn.dataset.variantId = firstAvailable.id;
          addBtn.textContent = 'Add to cart · ' + formatMoney(p.price);
        } else {
          addBtn.disabled = true;
          addBtn.textContent = 'Sold out';
        }
      })
      .catch(function (err) {
        console.error('[cw-product-card] quick-view fetch failed', err);
        title.textContent = 'Could not load preview.';
        desc.textContent = 'Try clicking through to the full product page.';
        addBtn.disabled = true;
        addBtn.textContent = 'Unavailable';
      });
  }

  function closeModal() {
    var modal = document.querySelector('.cw-quickview');
    if (!modal) return;
    if (modal.getAttribute('data-open') !== '1') return;  // already closed; no-op
    modal.setAttribute('data-open', '0');
    modal.setAttribute('aria-hidden', 'true');
    unlockScroll();
    // M23 focus-restore: return focus to the element that opened the modal
    if (lastTrigger && typeof lastTrigger.focus === 'function') {
      lastTrigger.focus();
    }
    lastTrigger = null;
  }

  // Bind triggers
  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-cw-quickview]');
    if (t) {
      e.preventDefault();
      openModal(t.dataset.cwQuickview, t);
      return;
    }
    var c = e.target.closest('[data-cw-quickview-close]');
    if (c) {
      closeModal();
      return;
    }
    var a = e.target.closest('[data-cw-quickview-add]');
    if (a && !a.disabled && a.dataset.variantId) {
      // Fix wave 68 (M22): explicit error handling on the quickview add
      // matching the wave-58/63 pattern in cw-cart-drawer.js + the existing
      // quick-add handler at the top of this file.
      var originalAddText = a.textContent;
      a.textContent = 'Adding…';
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(a.dataset.variantId, 10), quantity: 1 }] })
      })
        .then(function (r) {
          if (!r.ok) throw new Error('cart add not ok (' + r.status + ')');
          return r.json();
        })
        .then(function () {
          a.textContent = '✓ Added';
          window.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'cw-quickview' } }));
          setTimeout(closeModal, 900);
        })
        .catch(function (err) {
          console.error('[cw-product-card] quickview add failed', err);
          a.textContent = 'Error — retry';
          setTimeout(function () { a.textContent = originalAddText; }, 2200);
        });
    }
  });

  // Esc to close — only if this modal is actually open (wave 68 M19 partial)
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var modal = document.querySelector('.cw-quickview');
    if (modal && modal.getAttribute('data-open') === '1') closeModal();
  });

  // ─── Helpers ────────────────────────────────────────────────────────
  function formatMoney(cents) {
    if (typeof window.Shopify !== 'undefined' && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    // Naive fallback
    return '$' + (cents / 100).toFixed(2);
  }
})();
